import { AuthGuard, CheckPermissionsModes } from "@elit-hair/elit-tools";
import {
    Button,
    DatePicker,
    ErrorState,
    Icon,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarStats, TbChevronLeft, TbChevronRight } from "react-icons/tb";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useCall } from "@/providers/hooks/use-call";
import { BOOKING_STATUS, useBookings } from "@/queries/booking.queries";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { getHighestPriorityTeam, sortTeamsBySortOrder } from "@/utils/team.utils";

import { AppointmentAnalyticsStatusSummary } from "./components/appointment-analytics-status-summary";
import { AppointmentAnalyticsTimetable } from "./components/appointment-analytics-timetable";
import {
    buildAgentTimelineRows,
    buildAppointmentAnalyticsSummary,
    getTeamMemberIds,
} from "./utils/appointment-analytics.utils";

const AppointmentAnalyticsSearchSchema = z.object({
    team: z.string().optional(),
    date: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_APPOINTMENT_ANALYTICS)({
    component: AppointmentAnalyticsPage,
    validateSearch: zodValidator(AppointmentAnalyticsSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "appointment_analytics",
                to: APP_ROUTES_TO.PERFORMANCE_APPOINTMENT_ANALYTICS,
            },
            title: "appointment_analytics",
        },
        permissions: [PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function AppointmentAnalyticsPage() {
    return (
        <AuthGuard
            required={[PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE]}
            permissionMode={CheckPermissionsModes.ANY}
            fallback={null}
        >
            <AppointmentAnalyticsContent />
        </AuthGuard>
    );
}

function AppointmentAnalyticsContent() {
    const { t } = useTranslation();
    const { selectedBrand } = useCall();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const selectedDate = useMemo(() => {
        if (!search.date) {
            return DateTime.now().startOf("day");
        }

        const parsed = DateTime.fromISO(search.date);
        return parsed.isValid ? parsed.startOf("day") : DateTime.now().startOf("day");
    }, [search.date]);

    const dateKey = selectedDate.toFormat("yyyy-MM-dd");

    const updateSearch = useCallback(
        (updates: { team?: string; date?: string }) => {
            navigate({
                to: Route.to,
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    const { data: teamsData, isLoading: isLoadingTeams } = useTeams();
    const accessibleTeams = useMemo(() => sortTeamsBySortOrder(teamsData?.data || []), [teamsData?.data]);

    const selectedTeam = useMemo(
        () => accessibleTeams.find((team) => team.id === search.team) ?? null,
        [accessibleTeams, search.team]
    );

    useEffect(() => {
        if (search.date) {
            return;
        }

        updateSearch({ date: DateTime.now().toFormat("yyyy-MM-dd") });
    }, [search.date, updateSearch]);

    useEffect(() => {
        if (search.team || accessibleTeams.length === 0) {
            return;
        }

        const defaultTeam = getHighestPriorityTeam(accessibleTeams);
        if (defaultTeam) {
            updateSearch({ team: defaultTeam.id });
        }
    }, [accessibleTeams, search.team, updateSearch]);

    const hostIds = useMemo(() => (selectedTeam ? getTeamMemberIds(selectedTeam) : []), [selectedTeam]);
    const bookingHostIds = useMemo(() => (hostIds.length > 0 ? [...hostIds, null] : []), [hostIds]);

    const {
        data: dayBookings = [],
        isLoading: isLoadingBookings,
        isError: isBookingsError,
        error: bookingsError,
        refetch: refetchBookings,
    } = useBookings(
        {
            dateFrom: dateKey,
            dateTo: dateKey,
            hostIds: bookingHostIds,
            status: BOOKING_STATUS.CONFIRMED,
        },
        { enabled: bookingHostIds.length > 0 }
    );

    const assignedBookings = useMemo(() => dayBookings.filter((booking) => booking.hostId), [dayBookings]);
    const unassignedBookings = useMemo(() => dayBookings.filter((booking) => !booking.hostId), [dayBookings]);
    const allAppointments = dayBookings;

    const summary = useMemo(
        () => buildAppointmentAnalyticsSummary(allAppointments, selectedBrand),
        [allAppointments, selectedBrand]
    );

    const timelineRows = useMemo(
        () => buildAgentTimelineRows(selectedTeam, assignedBookings, unassignedBookings),
        [selectedTeam, assignedBookings, unassignedBookings]
    );

    const handleDateChange = useCallback(
        (nextDate: DateTime) => {
            updateSearch({ date: nextDate.toFormat("yyyy-MM-dd") });
        },
        [updateSearch]
    );

    const isLoading = isLoadingTeams || (Boolean(selectedTeam) && isLoadingBookings);
    const isError = isBookingsError;
    const error = bookingsError;

    return (
        <div className="flex h-full w-full flex-col gap-4 overflow-auto p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Icon icon={TbCalendarStats} size={18} className="text-primary" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold">{t("appointment_analytics")}</h1>
                        <p className="text-xs text-muted-foreground">{t("appointment_analytics_subtitle")}</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={selectedTeam?.id ?? ""}
                        onValueChange={(teamId) => updateSearch({ team: teamId })}
                        disabled={isLoadingTeams || accessibleTeams.length === 0}
                    >
                        <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder={t("select_team")} />
                        </SelectTrigger>
                        <SelectContent>
                            {accessibleTeams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="size-3 rounded-full border border-black/20 dark:border-white/30"
                                            style={{ backgroundColor: team.color }}
                                        />
                                        {team.name}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="inline-flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("previous_day")}
                            onClick={() => handleDateChange(selectedDate.minus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronLeft} size={16} />
                        </Button>
                        <DatePicker
                            date={selectedDate.toJSDate()}
                            onDateChange={(date) => {
                                if (!date) {
                                    return;
                                }

                                handleDateChange(DateTime.fromJSDate(date));
                            }}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            aria-label={t("next_day")}
                            onClick={() => handleDateChange(selectedDate.plus({ days: 1 }))}
                        >
                            <Icon icon={TbChevronRight} size={16} />
                        </Button>
                    </div>
                </div>
            </div>

            {!selectedTeam && !isLoadingTeams ? (
                <div className="rounded-xl border border-dashed border-border/60 px-4 py-10 text-center text-sm text-muted-foreground">
                    {t("select_team")}
                </div>
            ) : null}

            {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-16">
                    <Spinner />
                </div>
            ) : null}

            {isError ? (
                <ErrorState
                    title={t("error")}
                    message={error instanceof Error ? error.message : t("connection_error")}
                    onRetry={() => {
                        void refetchBookings();
                    }}
                    retryLabel={t("try_again")}
                />
            ) : null}

            {selectedTeam && !isLoading && !isError ? (
                <>
                    <AppointmentAnalyticsStatusSummary summary={summary} />
                    <AppointmentAnalyticsTimetable rows={timelineRows} brandName={selectedBrand} />
                </>
            ) : null}
        </div>
    );
}
